import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useJob, useUpdateJob, jobKeys } from '@/hooks/useJobs';
import { Loader2 } from 'lucide-react';
import { OfflinePill, SaveStatusPill, type SaveStatus } from '@/components/save/saveStatus';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { JobCardStatusBadge } from '@/components/shared/JobCardStatusBadge';
import { JobBillingStateBadge } from '@/components/billing/JobBillingStateBadge';
import { useJobBilling } from '@/hooks/useInvoicePayments';
import { JobDetailTabs, JOB_DETAIL_TABS, type Tab } from './components/overview/JobDetailTabs';
import { JobDetailOverview } from './components/overview/JobDetailOverview';
import { JobBreakdown } from './components/breakdown/JobBreakdown';
import { JobDetailExpenses } from './components/expenses/JobDetailExpenses';
import { InspectionReportsSection } from './components/inspections/InspectionReportsSection';
import { JobFilesSection } from './components/files/JobFilesSection';
import { JobDetailBilling } from './components/billing/JobDetailBilling';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { saveOffline, loadOffline, clearOffline, offlineOverviewKey } from '@/hooks/useOfflineStorage';
import { formatClientName, projectTypeToSlotKey, type BreakdownSlotKey } from '@jobos/shared';
import { BreakdownFlushProvider, useBreakdownFlush } from '@/contexts/BreakdownFlushContext';
import { breakdownKeys } from '@/hooks/useBreakdown';
import { useToast } from '@/hooks/use-toast';

const DEBOUNCE_MS = 400;
const SAVED_DISPLAY_MS = 2000;

const SLOT_LABELS: Record<BreakdownSlotKey, string> = {
  generic: 'Generic',
  carpet: 'Carpet',
};
function labelForSlot(key: BreakdownSlotKey): string {
  return SLOT_LABELS[key];
}

export default function JobDetail() {
  // Outer wrapper provides BreakdownFlushContext so the breakdown surfaces
  // (generic + single-category) can register flush handles that the slot-switch
  // path in JobDetailInner.executeSave will call inside its mutex.
  return (
    <BreakdownFlushProvider>
      <JobDetailInner />
    </BreakdownFlushProvider>
  );
}

function JobDetailInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: job, isLoading, error } = useJob(id);
  // The derived billing state for the header badge — the same rollup the
  // Billing tab reports, so the two can never disagree.
  const { data: billing } = useJobBilling(id);
  const { mutateAsync: updateJob } = useUpdateJob(id!, { showToast: false });
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Anything the tab list doesn't know about — a stale link, a typo — lands on
  // Overview rather than an empty content area.
  const activeTab: Tab =
    JOB_DETAIL_TABS.find((tab) => tab.id === tabParam)?.id ?? 'overview';
  const breakdownDirtyRef = useRef(false);
  const breakdownFlush = useBreakdownFlush();

  const setActiveTab = useCallback((tab: Tab) => {
    // Leaving the breakdown tab unmounts the editor, so persist pending edits.
    // Fire-and-forget rather than await: the save mutation captures the pending
    // data synchronously and completes in the background after unmount, so the
    // tab switch stays instant instead of blocking on a network round-trip. On
    // failure the local backup + recovery-on-return cover the edits.
    if (activeTab === 'breakdown' && tab !== 'breakdown' && breakdownDirtyRef.current) {
      void breakdownFlush.flush().catch((e) => console.error('background breakdown flush failed', e));
    }
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  }, [setSearchParams, activeTab, breakdownFlush]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const isOnline = useOnlineStatus();
  const previousOverflow = useRef<{ body: string; html: string } | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingUpdatesRef = useRef<Record<string, any>>({});
  const isSavingRef = useRef(false);
  const queuedUpdatesRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (!previousOverflow.current) {
      previousOverflow.current = {
        body: body.style.overflow,
        html: html.style.overflow,
      };
    }

    if (activeTab === 'breakdown') {
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    } else if (previousOverflow.current) {
      body.style.overflow = previousOverflow.current.body;
      html.style.overflow = previousOverflow.current.html;
    }

    return () => {
      if (previousOverflow.current) {
        body.style.overflow = previousOverflow.current.body;
        html.style.overflow = previousOverflow.current.html;
        previousOverflow.current = null;
      }
    };
  }, [activeTab]);

  // Pending flush resolve/reject callbacks so callers can await the actual save
  const flushResolversRef = useRef<Array<{ resolve: () => void; reject: (err: any) => void }>>([]);

  const { toast } = useToast();

  // Execute a save, serializing requests so only one is in-flight at a time
  const executeSave = useCallback(async (updates: Record<string, any>, resolvers: Array<{ resolve: () => void; reject: (err: any) => void }>) => {
    if (isSavingRef.current) {
      // Merge into queued updates instead of firing a concurrent request
      queuedUpdatesRef.current = { ...(queuedUpdatesRef.current || {}), ...updates };
      // Transfer resolvers so they resolve when the queued save completes
      flushResolversRef.current.push(...resolvers);
      return;
    }

    if (Object.keys(updates).length === 0) {
      resolvers.forEach((r) => r.resolve());
      return;
    }

    // If offline, persist to localStorage instead of calling API
    if (!navigator.onLine) {
      saveOffline(offlineOverviewKey(id!), updates);
      setSaveStatus('savedLocally');
      resolvers.forEach((r) => r.resolve());
      return;
    }

    isSavingRef.current = true;
    setSaveStatus('saving');
    try {
      // === Slot-change pre-flight, INSIDE the mutex ===
      const oldSlot = projectTypeToSlotKey(job?.projectType);
      const slotChanging =
        'projectType' in updates &&
        oldSlot !== projectTypeToSlotKey(updates.projectType);

      if (slotChanging) {
        // Persist any pending breakdown edits before swapping live tables.
        // Autosave-on-leave: flush immediately so changing project type never
        // loses in-progress work. If the underlying save errored, abort the
        // swap rather than snapshot stale state.
        try {
          await breakdownFlush.flush();
        } catch (flushErr) {
          isSavingRef.current = false;
          toast({
            title: "Couldn't save your current breakdown",
            description: 'Resolve the save error before changing project type.',
            variant: 'destructive',
          });
          resolvers.forEach((r) => r.reject(flushErr));
          setSaveStatus('error');
          return;
        }
      }
      // === end slot-change branch ===

      await updateJob(updates);
      clearOffline(offlineOverviewKey(id!));
      isSavingRef.current = false;
      resolvers.forEach((r) => r.resolve());

      // Re-evaluate after the await — Cancel may have dropped projectType.
      const actuallySwapped =
        'projectType' in updates &&
        oldSlot !== projectTypeToSlotKey(updates.projectType);
      if (actuallySwapped) {
        // Live breakdown tables and version list both reflect the new slot.
        await queryClient.invalidateQueries({ queryKey: breakdownKeys.detail(id!) });
        await queryClient.invalidateQueries({ queryKey: breakdownKeys.versions(id!) });
        // Breakdown documents are scoped to (jobId, slotKey) — the docId that
        // was open belongs to the old slot, so clear it from the URL. The
        // wrapper's resolver re-runs on the new slot and either opens the
        // single-doc editor, shows the list, or auto-creates Working.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('docId');
            return next;
          },
          { replace: true },
        );
        toast({
          title: `Switched to ${updates.projectType}`,
          description: `Your ${labelForSlot(oldSlot)} breakdown is saved.`,
        });
      }

      // If there are queued updates, patch the cache so the server response
      // doesn't clobber optimistic local state for fields about to be saved
      if (queuedUpdatesRef.current) {
        queryClient.setQueryData(jobKeys.detail(id!), (old: any) =>
          old ? { ...old, ...queuedUpdatesRef.current } : old
        );
      }

      // Process any updates that queued while we were saving
      if (queuedUpdatesRef.current) {
        const queued = queuedUpdatesRef.current;
        const queuedResolvers = flushResolversRef.current;
        queuedUpdatesRef.current = null;
        flushResolversRef.current = [];
        executeSave(queued, queuedResolvers);
      } else {
        setSaveStatus('saved');
        savedTimerRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, SAVED_DISPLAY_MS);
      }
    } catch (err) {
      isSavingRef.current = false;
      // Network error (not server error) — persist offline
      if (err instanceof TypeError) {
        saveOffline(offlineOverviewKey(id!), updates);
        setSaveStatus('savedLocally');
        resolvers.forEach((r) => r.resolve());
      } else {
        setSaveStatus('error');
        resolvers.forEach((r) => r.reject(err));
        savedTimerRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, SAVED_DISPLAY_MS);
      }

      // Still process queued updates on error (they may be independent changes)
      if (queuedUpdatesRef.current) {
        const queued = queuedUpdatesRef.current;
        const queuedResolvers = flushResolversRef.current;
        queuedUpdatesRef.current = null;
        flushResolversRef.current = [];
        setTimeout(() => executeSave(queued, queuedResolvers), 1000);
      }
    }
  }, [updateJob, id, queryClient, job, breakdownFlush, toast]);

  // Debounced update function - batches multiple field changes into single API call
  const updateField = useCallback((field: string, value: any): Promise<void> => {
    // Accumulate pending updates
    pendingUpdatesRef.current[field] = value;
    setSaveStatus('dirty');

    // Clear existing timers
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
    }

    // Return a promise that resolves when the debounced save actually completes
    const promise = new Promise<void>((resolve, reject) => {
      flushResolversRef.current.push({ resolve, reject });
    });

    // Set new timer to flush all pending updates
    debounceTimerRef.current = setTimeout(() => {
      const updates = pendingUpdatesRef.current;
      const resolvers = flushResolversRef.current;
      pendingUpdatesRef.current = {};
      flushResolversRef.current = [];
      executeSave(updates, resolvers);
    }, DEBOUNCE_MS);

    return promise;
  }, [executeSave]);

  // Flush pending updates on unmount
  const executeSaveRef = useRef(executeSave);
  executeSaveRef.current = executeSave;
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
      // Flush any pending updates immediately
      const updates = { ...pendingUpdatesRef.current, ...(queuedUpdatesRef.current || {}) };
      pendingUpdatesRef.current = {};
      queuedUpdatesRef.current = null;
      const resolvers = flushResolversRef.current;
      flushResolversRef.current = [];
      if (Object.keys(updates).length > 0) {
        executeSaveRef.current(updates, resolvers);
      }
    };
  }, []);

  // Flush offline overview edits when connectivity returns or on mount
  useEffect(() => {
    const flushOfflineOverview = async () => {
      if (!id) return;
      const offlineUpdates = loadOffline<Record<string, any>>(offlineOverviewKey(id));
      if (!offlineUpdates) return;

      setSaveStatus('saving');
      try {
        await updateJob(offlineUpdates);
        clearOffline(offlineOverviewKey(id));
        setSaveStatus('saved');
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), SAVED_DISPLAY_MS);
      } catch {
        setSaveStatus('error');
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), SAVED_DISPLAY_MS);
      }
    };

    // Flush on mount if online
    if (isOnline) {
      flushOfflineOverview();
    }

    // Flush when coming back online
    const handleOnline = () => flushOfflineOverview();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [id, updateJob, isOnline]);

  // Render fixed save status indicator
  const renderSaveStatus = () => {
    if (saveStatus === 'idle' && isOnline) return null;
    if (saveStatus === 'savedLocally' && isOnline) return null; // will flush momentarily

    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2">
        {!isOnline && <OfflinePill />}
        <SaveStatusPill status={saveStatus} />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">
          Error loading job: {error?.message || 'Job not found'}
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    navigate('/jobs');
  };

  return (
    // App-shell layout: the page itself never scrolls. The header (above) and
    // this tab nav stay pinned; the content area below is the single scroll
    // container, so every tab scrolls internally. This keeps the nav in normal
    // flow (no sticky / z-index games) and works uniformly across tabs —
    // breakdown's editor panes simply manage their own nested scroll.
    // `print:*` overrides drop the fixed height + overflow so native print
    // (the overview's print view) flows across pages instead of clipping.
    <div className="bg-gray-50 flex flex-col h-[calc(100vh-var(--app-header-height,0px))] overflow-hidden print:h-auto print:overflow-visible print:block">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-1 w-full max-w-7xl">
        <JobDetailTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          leftContent={
            <>
              <button
                onClick={() => {
                  // Fire-and-forget pending breakdown save so navigation stays
                  // instant (see setActiveTab for rationale).
                  if (activeTab === 'breakdown' && breakdownDirtyRef.current) {
                    void breakdownFlush.flush().catch((e) => console.error('background breakdown flush failed', e));
                  }
                  const fromJobList = (location.state as { fromJobList?: boolean })?.fromJobList;
                  if (fromJobList) {
                    // Go back to preserve URL query params (filters, etc.)
                    navigate(-1);
                  } else {
                    // Navigate directly to job list
                    navigate('/jobs');
                  }
                }}
                className="text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                ← Back
              </button>
              {/* On a phone the client name is worth a second line — truncating
                  it hides the one field that tells you whose job this is. The
                  desktop row has no spare height, so it still clips. */}
              <span className="min-w-0 break-words text-sm font-medium text-gray-700 sm:truncate">
                {job.jobCode}{job.client ? ` · ${formatClientName(job.client.name, job.client.firstName, job.client.lastName)}` : ''}
              </span>
            </>
          }
          rightContent={
            <>
              <JobStatusBadge status={job.status} />
              <JobCardStatusBadge status={job.jobCardStatus} />
              {/* The job detail response carries the rollup, so the badge
                  reads the same derived state the Billing tab reports. */}
              <JobBillingStateBadge state={billing?.billingState} />
            </>
          }
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto print:overflow-visible print:min-h-0">
        {activeTab === 'overview' ? (
          <div className="mx-auto px-4 sm:px-6 lg:px-8 w-full max-w-7xl">
            <JobDetailOverview
              job={job}
              onUpdateField={updateField}
              onDelete={handleDelete}
            />
          </div>
        ) : activeTab === 'breakdown' ? (
          // Breakdown fills the fixed height and scrolls its own panes/list
          // internally, so it keeps h-full rather than growing the outer scroll.
          <div className="mx-auto h-full px-4 sm:px-6 lg:px-8 pb-6 box-border w-full flex flex-col">
            <JobBreakdown
              job={job}
              onDirtyChange={(dirty) => { breakdownDirtyRef.current = dirty; }}
            />
          </div>
        ) : activeTab === 'expenses' ? (
          <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-6 w-full max-w-7xl">
            <JobDetailExpenses jobId={id!} />
          </div>
        ) : activeTab === 'billing' ? (
          <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-6 w-full max-w-7xl">
            <JobDetailBilling
              jobId={job.id}
              jobCode={job.jobCode || ''}
              jobDescription={job.description ?? null}
              clientName={formatClientName(job.client?.name, job.client?.firstName, job.client?.lastName)}
            />
          </div>
        ) : activeTab === 'inspections' ? (
          <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-6 w-full max-w-7xl">
            <InspectionReportsSection jobId={id!} />
          </div>
        ) : activeTab === 'files' ? (
          <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-6 w-full max-w-7xl">
            <JobFilesSection jobId={id!} />
          </div>
        ) : null}
      </div>

      {renderSaveStatus()}
    </div>
  );
}
